import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Link as RouterLink } from "react-router-dom";
import {
  Box,
  Breadcrumbs,
  Link,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { tokens } from "../../theme";
import { PageHeader } from "../PageHeader";
import type { Translate } from "../../i18n/translate";

/**
 * The Start & navigation page frame (NendaPortal_1 frames 1748:20043 /
 * 1751:20427 / 1756:21275): breadcrumbs, a section title, then three columns -
 * what you can pick, the editor for what you picked, and how it looks on the
 * TV. Appearance uses it; Start view is a table with a drawer instead, and
 * shares the controls below (SegmentedChoice, ActiveChoice, FieldBlock ...).
 */

export interface Crumb {
  label: string;
  /** Absent on the last crumb - the page you are on. */
  to?: string;
}

/** The first crumb of every Start & navigation page. */
export function startNavRoot(t: Translate): Crumb {
  return {
    label: t("common:labels.startAndNavigation"),
    to: "/frontlayer/home",
  };
}

export function StartNavLayout({
  crumbs,
  pageTitle,
  pageSubtitle,
  title,
  subtitle,
  list,
  editor,
  preview,
  actions,
  notice,
}: {
  crumbs: Crumb[];
  /** The sub-menu you are in - rendered in the app header. */
  pageTitle: string;
  pageSubtitle?: string;
  /** The level you are on inside it. */
  title: string;
  subtitle?: string;
  list: ReactNode;
  editor: ReactNode;
  /** Omitted for a page with nothing to preview; the editor takes its room. */
  preview?: ReactNode;
  /** Right of the title (Information's "Save changes"). */
  actions?: ReactNode;
  /** Between the title and the columns - a save error, say. */
  notice?: ReactNode;
}) {
  const { t } = useTranslation(["templates", "common"]);
  return (
    <Box
      sx={{ px: { xs: 2, md: 4 }, py: { xs: 2.5, md: 3 } }}
      data-testid="startnav-page"
    >
      <PageHeader title={pageTitle} subtitle={pageSubtitle} />
      <Breadcrumbs
        separator={<ChevronRightIcon sx={{ fontSize: 18 }} />}
        aria-label={t("startNavLayout.breadcrumb")}
        sx={{
          mb: 3,
          "& .MuiBreadcrumbs-separator": { color: tokens.brand, mx: 0.5 },
        }}
        data-testid="startnav-breadcrumbs"
      >
        {crumbs.map((c, i) =>
          c.to && i < crumbs.length - 1 ? (
            <Link
              key={c.label}
              component={RouterLink}
              to={c.to}
              underline="hover"
              sx={{
                color: tokens.brand,
                fontSize: 15,
                fontWeight: "fontWeightMedium",
              }}
            >
              {c.label}
            </Link>
          ) : (
            <Typography
              key={c.label}
              aria-current="page"
              sx={{
                color: tokens.brand,
                fontWeight: "fontWeightMedium",
                borderBottom: "2px solid",
                borderColor: tokens.brand,
                lineHeight: 1.6,
              }}
            >
              {c.label}
            </Typography>
          )
        )}
      </Breadcrumbs>

      <Stack
        direction="row"
        alignItems="flex-start"
        justifyContent="space-between"
        spacing={2}
        sx={{ mb: 4 }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography
            component="h2"
            sx={{
              typography: "headline",
              fontWeight: "fontWeightSemibold",
              lineHeight: 1.3,
            }}
            data-testid="startnav-title"
          >
            {title}
          </Typography>
          {subtitle && (
            <Typography
              variant="body2"
              sx={{ mt: 0.75, color: tokens.inkMuted }}
            >
              {subtitle}
            </Typography>
          )}
        </Box>
        {actions && <Box sx={{ flexShrink: 0 }}>{actions}</Box>}
      </Stack>
      {notice}

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            md: "minmax(220px, 280px) minmax(0, 1fr)",
            lg: preview
              ? "minmax(220px, 260px) minmax(0, 1fr) minmax(260px, 360px)"
              : "minmax(240px, 280px) minmax(0, 1fr)",
          },
          columnGap: 3,
          rowGap: 4,
          alignItems: "start",
        }}
      >
        <Box
          sx={{
            pr: { md: 3 },
            borderRight: { md: "1px solid" },
            borderColor: { md: tokens.divider },
            alignSelf: "stretch",
          }}
        >
          {list}
        </Box>
        <Box
          sx={{
            minWidth: 0,
            pr: { lg: preview ? 3 : 0 },
            borderRight: { lg: preview ? "1px solid" : "none" },
            borderColor: { lg: tokens.divider },
            alignSelf: "stretch",
          }}
          data-testid="startnav-editor"
        >
          {editor}
        </Box>
        {preview && (
          <Box
            sx={{
              minWidth: 0,
              // Below lg the preview drops under the editor, spanning both
              // columns, instead of squeezing the form into a strip.
              gridColumn: { md: "1 / -1", lg: "auto" },
              position: { lg: "sticky" },
              top: { lg: "calc(var(--foyer-app-header-h, 0px) + 16px)" },
            }}
          >
            {preview}
          </Box>
        )}
      </Box>
    </Box>
  );
}

/** A column's heading: a title and one muted line under it. */
export function PanelHeading({
  title,
  subtitle,
}: {
  title: string;
  subtitle?: string;
}) {
  return (
    <Box sx={{ mb: 3 }}>
      <Typography
        component="h3"
        sx={{ typography: "title", fontWeight: "fontWeightSemibold" }}
      >
        {title}
      </Typography>
      {subtitle && (
        <Typography variant="bodySm" sx={{ mt: 0.5, color: tokens.inkMuted }}>
          {subtitle}
        </Typography>
      )}
    </Box>
  );
}

/** A labelled field block inside the editor column (title + helper line). */
export function FieldBlock({
  label,
  help,
  children,
}: {
  label: string;
  help?: string;
  children: ReactNode;
}) {
  return (
    <Box sx={{ mb: 4 }}>
      <Typography variant="body2" sx={{ fontWeight: "fontWeightSemibold" }}>
        {label}
      </Typography>
      {help && (
        <Typography variant="bodySm" sx={{ mt: 0.25, color: tokens.inkMuted }}>
          {help}
        </Typography>
      )}
      <Box sx={{ mt: 1.5 }}>{children}</Box>
    </Box>
  );
}

/**
 * Two-way segmented choice - the design's "Ja / Nej" and "Celsius /
 * Fahrenheit". The selected half is filled with the primary colour.
 */
export function SegmentedChoice<T extends string>({
  value,
  options,
  onChange,
  disabled,
  testId,
}: {
  value: T;
  options: { value: T; label: string }[];
  onChange: (next: T) => void;
  disabled?: boolean;
  testId?: string;
}) {
  return (
    <ToggleButtonGroup
      exclusive
      value={value}
      disabled={disabled}
      onChange={(_e, next: T | null) => {
        if (next !== null) onChange(next);
      }}
      data-testid={testId}
      sx={{
        // NendaPortal_1 Yes/No: two 82x40 halves, 12px semibold, #E6EDF5
        // border, 8px outer radius; the chosen half filled brand.
        "& .MuiToggleButton-root": {
          minWidth: 82,
          height: 40,
          px: 2,
          textTransform: "none",
          typography: "meta",
          fontWeight: "fontWeightSemibold",
          lineHeight: "20px",
          color: tokens.ink,
          borderColor: tokens.line,
          bgcolor: tokens.surface,
        },
        "& .MuiToggleButton-root:first-of-type": {
          borderRadius: "8px 0 0 8px",
        },
        "& .MuiToggleButton-root:last-of-type": {
          borderRadius: "0 8px 8px 0",
        },
        "& .MuiToggleButton-root.Mui-selected, & .MuiToggleButton-root.Mui-selected:hover":
          {
            bgcolor: tokens.brand,
            color: "#fff",
            borderColor: tokens.line,
          },
      }}
    >
      {options.map((o) => (
        <ToggleButton
          key={o.value}
          value={o.value}
          data-testid={testId ? `${testId}-${o.value}` : undefined}
        >
          {o.label}
        </ToggleButton>
      ))}
    </ToggleButtonGroup>
  );
}

/**
 * The "Shown on the home screen: Yes / No" row every switchable section starts
 * with (design: "Visas i startvyn"). Text on the left, the choice on the right.
 */
export function ActiveChoice({
  label,
  title,
  on,
  onChange,
  help,
  fixedNote,
}: {
  label: string;
  /** The row's heading. */
  title?: string;
  on: boolean;
  onChange?: (on: boolean) => void;
  help?: string;
  /** Replaces the control when the section cannot be switched. */
  fixedNote?: string;
}) {
  const { t } = useTranslation(["templates", "common"]);
  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      spacing={3}
      sx={{ mb: 4 }}
      data-testid="startnav-shown"
    >
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography
          sx={{ typography: "titleSm", fontWeight: "fontWeightSemibold" }}
        >
          {title ?? t("startNavLayout.active")}
        </Typography>
        <Typography variant="bodySm" sx={{ mt: 0.5, color: tokens.inkMuted }}>
          {help ?? t("startNavLayout.controlsWhether", { name: label })}
        </Typography>
      </Box>
      {fixedNote || !onChange ? (
        <Typography
          variant="bodySm"
          sx={{ color: tokens.inkMuted, flexShrink: 0 }}
          data-testid="startnav-fixed-note"
        >
          {fixedNote ?? t("startNavLayout.alwaysShown")}
        </Typography>
      ) : (
        <Box sx={{ flexShrink: 0 }}>
          <SegmentedChoice
            value={on ? "yes" : "no"}
            options={[
              { value: "yes", label: t("common:buttons.yes") },
              { value: "no", label: t("common:buttons.no") },
            ]}
            onChange={(v) => onChange(v === "yes")}
            testId="startnav-active"
          />
        </Box>
      )}
    </Stack>
  );
}

/** The tinted info callout ("Plats för väderdata"). */
export function InfoCallout({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <Stack
      direction="row"
      spacing={1.5}
      sx={{
        p: 2,
        borderRadius: "8px",
        border: "1px solid",
        borderColor: tokens.brand,
        bgcolor: tokens.brandTintSoft,
      }}
    >
      <InfoOutlinedIcon sx={{ color: tokens.brand, mt: 0.25 }} />
      <Box>
        <Typography sx={{ fontWeight: "fontWeightSemibold" }}>
          {title}
        </Typography>
        <Typography variant="bodySm" sx={{ mt: 0.25, color: tokens.inkMuted }}>
          {children}
        </Typography>
      </Box>
    </Stack>
  );
}

export interface ListItem {
  key: string;
  label: string;
}

export interface ListGroup {
  key: string;
  title: string;
  subtitle?: string;
  items: ListItem[];
}

/**
 * The left column: one or more titled groups of selectable rows. (Start view's
 * menu items, which reorder and carry a status, are a table of their own.)
 */
export function SectionList({
  groups,
  selected,
  onSelect,
}: {
  groups: ListGroup[];
  selected?: string;
  onSelect: (key: string) => void;
}) {
  const { t } = useTranslation(["templates", "common"]);
  return (
    <Stack spacing={4} data-testid="startnav-list">
      {groups.map((g) => (
        <Box key={g.key} data-testid={`startnav-group-${g.key}`}>
          <PanelHeading
            title={t("startNavLayout.groupTitle", {
              title: g.title,
              count: g.items.length,
            })}
            subtitle={g.subtitle}
          />
          <Stack spacing={0.75}>
            {g.items.map((item) => (
              <SectionRow
                key={item.key}
                item={item}
                selected={item.key === selected}
                onSelect={() => onSelect(item.key)}
              />
            ))}
          </Stack>
        </Box>
      ))}
    </Stack>
  );
}

function SectionRow({
  item,
  selected,
  onSelect,
}: {
  item: ListItem;
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <Box
      role="button"
      tabIndex={0}
      aria-current={selected ? "true" : undefined}
      onClick={onSelect}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onSelect();
        }
      }}
      data-testid={`startnav-item-${item.key}`}
      sx={{
        display: "flex",
        alignItems: "center",
        minHeight: 50,
        px: 1.5,
        borderRadius: "8px",
        cursor: "pointer",
        bgcolor: selected ? tokens.brandSelected : "transparent",
        "&:hover": {
          bgcolor: selected ? tokens.brandSelected : tokens.brandTintSoft,
        },
        "&:focus-visible": {
          outline: `2px solid ${tokens.brand}`,
          outlineOffset: 1,
        },
      }}
    >
      <Typography
        sx={{
          flex: 1,
          minWidth: 0,
          fontWeight: "fontWeightMedium",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {item.label}
      </Typography>
    </Box>
  );
}

/** Start screen vs inside Information, for a screen the template lets move. */
export function PlacementChoice({
  insideInfo,
  onMove,
}: {
  insideInfo: boolean;
  onMove: () => void;
}) {
  const { t } = useTranslation(["templates", "common"]);
  return (
    <FieldBlock
      label={t("startNavLayout.placement")}
      help={t("startNavLayout.placementHelp")}
    >
      <SegmentedChoice
        value={insideInfo ? "info" : "home"}
        options={[
          { value: "home", label: t("startNavLayout.startScreen") },
          { value: "info", label: t("startNavLayout.insideInformation") },
        ]}
        onChange={onMove}
        testId="menu-item-placement"
      />
    </FieldBlock>
  );
}
